/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/api/complianceService.ts
 * PURPOSE: Soft Compliance API Service - Sprint 17 GOLIVE
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 7: Performance SLAs (<50ms p95)
 *
 * DATE: 2025-12-26
 * COMPLIANCE: SDLC 6.3.1 + Expert 3 Feedback + Vietnamese Labor Law 2019
 *
 * @business-context
 * - Domain: Vietnamese F&B Workforce Compliance
 * - Impact: Enable soft compliance with manager override capability
 * - Stakeholders: Branch Managers, HR Directors, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, Axios Client, Type-Safe API
 * - Dependencies: Axios, TypeScript, Backend Compliance APIs
 *
 * @cross-references
 * - Backend: backend/app/api/v1/compliance_override.py
 * - Design: docs/02-design/05-business-logic/SOFT-COMPLIANCE-WARNING-DESIGN.md
 */

import { apiClient } from './client';

// ============= Compliance Severity Tiers =============

export type ComplianceSeverity = 'INFO' | 'WARNING' | 'VIOLATION' | 'CRITICAL';

export type ViolationType =
  | 'WEEKLY_HOURS_INFO'
  | 'WEEKLY_HOURS_WARNING'
  | 'WEEKLY_HOURS_VIOLATION'
  | 'WEEKLY_HOURS_CRITICAL'
  | 'DAILY_HOURS_INFO'
  | 'DAILY_HOURS_WARNING'
  | 'DAILY_HOURS_VIOLATION'
  | 'DAILY_HOURS_CRITICAL'
  | 'CONSECUTIVE_DAYS_INFO'
  | 'CONSECUTIVE_DAYS_WARNING'
  | 'CONSECUTIVE_DAYS_VIOLATION'
  | 'CONSECUTIVE_DAYS_CRITICAL'
  | 'REST_PERIOD_INFO'
  | 'REST_PERIOD_WARNING'
  | 'REST_PERIOD_VIOLATION'
  | 'REST_PERIOD_CRITICAL'
  | 'OVERTIME_MONTHLY_WARNING'
  | 'OVERTIME_MONTHLY_VIOLATION'
  | 'OVERTIME_MONTHLY_CRITICAL'
  | 'SPLIT_SHIFT_GAP_WARNING'
  | 'COMPLIANT';

export type OverrideReasonCategory =
  | 'SICK_LEAVE_COVER'
  | 'EMERGENCY_STAFFING'
  | 'SEASONAL_PEAK'
  | 'SPECIAL_EVENT'
  | 'EMPLOYEE_REQUEST'
  | 'OPERATIONAL_NECESSITY'
  | 'OTHER';

export type OverrideRole =
  | 'branch_manager'
  | 'hr_director'
  | 'operations_director'
  | 'ceo';

export type OverrideStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'EXPIRED';

// ============= Type Definitions =============

export interface ComplianceResult {
  severity: ComplianceSeverity;
  violation_type: ViolationType;
  message_vi: string;
  message_en: string;
  current_value: number;
  limit_value: number;
  excess_value: number;
  can_override: boolean;
  override_role: OverrideRole | null;
  requires_reason: boolean;
  audit_required: boolean;
  employee_id: string | null;
  employee_name: string | null;
  schedule_date: string | null;
  validated_at: string;
}

export interface ComplianceValidationRequest {
  employee_id: string;
  branch_id?: string;
  current_weekly_hours: number;
  current_daily_hours: number;
  current_consecutive_days: number;
  current_monthly_overtime: number;
  new_shift_hours: number;
  new_shift_date?: string;
  previous_shift_end?: string;
  new_shift_start?: string;
}

export interface ComplianceValidationResponse {
  is_compliant: boolean;
  highest_severity: ComplianceSeverity;
  total_violations: number;
  results: ComplianceResult[];
  blocking_violations: ComplianceResult[];
  overridable_violations: ComplianceResult[];
  informational: ComplianceResult[];
  validated_at: string;
}

export interface OverrideRequest {
  employee_id: string;
  schedule_date: string;
  violation_type: ViolationType;
  severity: ComplianceSeverity;
  reason_category: OverrideReasonCategory;
  reason_text: string;
  branch_id?: string;
}

export interface OverrideResponse {
  id: string;
  status: OverrideStatus;
  message: string;
  employee_id: string;
  violation_type: ViolationType;
  severity: ComplianceSeverity;
  rejection_reason: string | null;
  created_at: string;
  processed_at: string | null;
}

export interface OverrideAuditLog {
  id: string;
  tenant_id: number;
  branch_id: string | null;
  employee_id: string;
  employee_name: string;
  employee_code: string | null;
  violation_type: ViolationType;
  severity: ComplianceSeverity;
  original_value: number;
  limit_value: number;
  reason_category: OverrideReasonCategory;
  reason_text: string;
  approved_by: string;
  approved_by_name: string;
  approved_by_role: OverrideRole;
  status: OverrideStatus;
  created_at: string;
  ip_address: string | null;
  user_agent: string | null;
}

export interface AuditLogListResponse {
  total: number;
  items: OverrideAuditLog[];
  page: number;
  page_size: number;
}

export interface OverrideStatistics {
  total_overrides: number;
  by_severity: Record<ComplianceSeverity, number>;
  by_status: Record<OverrideStatus, number>;
  by_reason_category: Record<OverrideReasonCategory, number>;
  by_violation_type: Record<string, number>;
  by_approver_role: Record<OverrideRole, number>;
  date_range: {
    from: string | null;
    to: string | null;
  };
}

// ============= API Service =============

const COMPLIANCE_BASE_URL = '/api/v1/compliance';

/**
 * Validate compliance for a scheduling decision
 */
export async function validateCompliance(
  request: ComplianceValidationRequest
): Promise<ComplianceValidationResponse> {
  const response = await apiClient.post<ComplianceValidationResponse>(
    `${COMPLIANCE_BASE_URL}/validate`,
    request
  );
  return response.data;
}

/**
 * Request compliance override
 */
export async function requestOverride(
  request: OverrideRequest
): Promise<OverrideResponse> {
  const response = await apiClient.post<OverrideResponse>(
    `${COMPLIANCE_BASE_URL}/override`,
    request
  );
  return response.data;
}

/**
 * Get compliance override audit logs
 */
export async function getAuditLogs(params: {
  employee_id?: string;
  date_from?: string;
  date_to?: string;
  severity?: ComplianceSeverity;
  status?: OverrideStatus;
  page?: number;
  page_size?: number;
}): Promise<AuditLogListResponse> {
  const response = await apiClient.get<AuditLogListResponse>(
    `${COMPLIANCE_BASE_URL}/audit-log`,
    { params }
  );
  return response.data;
}

/**
 * Get single audit log entry
 */
export async function getAuditLog(logId: string): Promise<OverrideAuditLog> {
  const response = await apiClient.get<OverrideAuditLog>(
    `${COMPLIANCE_BASE_URL}/audit-log/${logId}`
  );
  return response.data;
}

/**
 * Get override statistics
 */
export async function getOverrideStatistics(params: {
  date_from?: string;
  date_to?: string;
}): Promise<OverrideStatistics> {
  const response = await apiClient.get<OverrideStatistics>(
    `${COMPLIANCE_BASE_URL}/statistics`,
    { params }
  );
  return response.data;
}

// ============= Helper Functions =============

/**
 * Get severity color for UI display
 */
export function getSeverityColor(severity: ComplianceSeverity): string {
  switch (severity) {
    case 'INFO':
      return 'var(--info)';
    case 'WARNING':
      return 'var(--warning)';
    case 'VIOLATION':
      return 'var(--primary-500)';
    case 'CRITICAL':
      return 'var(--error)';
    default:
      return 'var(--gray-500)';
  }
}

/**
 * Get severity icon name
 */
export function getSeverityIcon(severity: ComplianceSeverity): string {
  switch (severity) {
    case 'INFO':
      return 'info-circle';
    case 'WARNING':
      return 'exclamation-triangle';
    case 'VIOLATION':
      return 'exclamation-circle';
    case 'CRITICAL':
      return 'times-circle';
    default:
      return 'question-circle';
  }
}

/**
 * Get Vietnamese label for severity
 */
export function getSeverityLabelVi(severity: ComplianceSeverity): string {
  switch (severity) {
    case 'INFO':
      return 'Thông tin';
    case 'WARNING':
      return 'Cảnh báo';
    case 'VIOLATION':
      return 'Vi phạm';
    case 'CRITICAL':
      return 'Nghiêm trọng';
    default:
      return 'Không xác định';
  }
}

/**
 * Get Vietnamese label for override reason category
 */
export function getReasonCategoryLabelVi(
  category: OverrideReasonCategory
): string {
  switch (category) {
    case 'SICK_LEAVE_COVER':
      return 'Thay thế nghỉ ốm';
    case 'EMERGENCY_STAFFING':
      return 'Nhân sự khẩn cấp';
    case 'SEASONAL_PEAK':
      return 'Mùa cao điểm';
    case 'SPECIAL_EVENT':
      return 'Sự kiện đặc biệt';
    case 'EMPLOYEE_REQUEST':
      return 'Yêu cầu nhân viên';
    case 'OPERATIONAL_NECESSITY':
      return 'Nhu cầu vận hành';
    case 'OTHER':
      return 'Lý do khác';
    default:
      return 'Không xác định';
  }
}

/**
 * Check if user role can override a specific severity
 */
export function canRoleOverride(
  userRole: OverrideRole,
  severity: ComplianceSeverity
): boolean {
  const roleHierarchy: Record<OverrideRole, number> = {
    branch_manager: 1,
    operations_director: 2,
    hr_director: 3,
    ceo: 4,
  };

  const severityRequirements: Record<ComplianceSeverity, number> = {
    INFO: 0,
    WARNING: 1,
    VIOLATION: 3,
    CRITICAL: 999, // Cannot override
  };

  return roleHierarchy[userRole] >= severityRequirements[severity];
}

// ============= Export =============

export const complianceService = {
  validateCompliance,
  requestOverride,
  getAuditLogs,
  getAuditLog,
  getOverrideStatistics,
  getSeverityColor,
  getSeverityIcon,
  getSeverityLabelVi,
  getReasonCategoryLabelVi,
  canRoleOverride,
};

export default complianceService;
